interface ResponseData {
  ok: boolean;
  status: number;
  statusText: string;
  redirected?: boolean;
  url?: string;
  text: () => Promise<string>;
}

class ApiResponseError extends Error {
  constructor(
    message: string,
    readonly responseBody: string,
    readonly response: ResponseData
  ) {
    super(message);
    this.name = "ApiResponseError";
  }
}

const getResponseHost = (response: ResponseData): string | undefined => {
  if (!response.url) return;

  try {
    return new URL(response.url).hostname;
  } catch {
    return;
  }
};

export const isGoogleContentRedirectError = (error: unknown): boolean =>
  error instanceof ApiResponseError &&
  error.response.redirected === true &&
  getResponseHost(error.response) === "script.googleusercontent.com" &&
  /(?:ファイルを開くことができません|Sorry,\s+unable to open the file)/i.test(
    error.responseBody
  );

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const value = error as Record<string, unknown>;
    if (typeof value.message === "string") return value.message;
    if (typeof value.error === "string") return value.error;
    if (value.error !== undefined) return getErrorMessage(value.error);

    try {
      const serializedError = JSON.stringify(error);
      if (serializedError) return serializedError;
    } catch {
      // JSON に変換できないオブジェクトは共通メッセージにフォールバックする。
    }
  }
  return String(error);
};

export const formatErrorForDisplay = (error: unknown): string => {
  const message = getErrorMessage(error);
  if (!(error instanceof ApiResponseError)) return message;

  const details = [
    `Status: ${error.response.status}${
      error.response.statusText ? ` ${error.response.statusText}` : ""
    }`,
  ];
  if (typeof error.response.redirected === "boolean") {
    details.push(`Redirected: ${error.response.redirected ? "yes" : "no"}`);
  }
  const finalHost = getResponseHost(error.response);
  if (finalHost) details.push(`Final host: ${finalHost}`);
  if (!error.responseBody) return `${message}\n\n${details.join("\n")}`;

  return `${message}\n\n${details.join("\n")}\n\nResponse body:\n${error.responseBody}`;
};

const extractHtmlBodyText = (responseText: string): string | undefined => {
  if (!/<(?:!doctype\s+html|html|body)\b/i.test(responseText)) return;

  const bodyMatch = responseText.match(/<body\b[^>]*>([\s\S]*?)<\/body\s*>/i);
  if (!bodyMatch) return;

  const entityValues: Record<string, string> = {
    amp: "&",
    apos: "'",
    gt: ">",
    lt: "<",
    nbsp: " ",
    quot: '"',
  };
  const bodyText = bodyMatch[1]
    .replace(
      /<(script|style|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi,
      " "
    )
    .replace(/<[^>]+>/g, " ")
    .replace(/&#(\d+);/g, (_entity, code: string) =>
      String.fromCodePoint(Number(code))
    )
    .replace(/&#x([\da-f]+);/gi, (_entity, code: string) =>
      String.fromCodePoint(Number.parseInt(code, 16))
    )
    .replace(/&([a-z]+);/gi, (entity, name: string) =>
      name.toLowerCase() in entityValues
        ? entityValues[name.toLowerCase()]
        : entity
    )
    .replace(/\s+/g, " ")
    .trim();

  return bodyText || undefined;
};

const getResponseError = async (response: ResponseData): Promise<Error> => {
  const fallback = `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`;

  try {
    const text = await response.text();
    if (!text) return new ApiResponseError(fallback, "", response);

    try {
      return new ApiResponseError(
        getErrorMessage(JSON.parse(text)),
        text,
        response
      );
    } catch {
      const htmlBodyText = extractHtmlBodyText(text);
      if (htmlBodyText)
        return new ApiResponseError(fallback, htmlBodyText, response);
      return new ApiResponseError(text, text, response);
    }
  } catch {
    return new ApiResponseError(fallback, "", response);
  }
};

export const parseResponse = async (response: ResponseData) => {
  if (!response.ok) throw await getResponseError(response);

  const rawResponse = await response.text();
  let json: unknown;
  try {
    json = JSON.parse(rawResponse) as unknown;
  } catch (error) {
    const htmlBodyText = extractHtmlBodyText(rawResponse);
    if (htmlBodyText) {
      throw new ApiResponseError(
        "JSON の代わりに HTML が返されました。",
        htmlBodyText,
        response
      );
    }
    throw error;
  }
  if (json && typeof json === "object" && "error" in json && json.error) {
    throw new ApiResponseError(
      getErrorMessage(json.error),
      rawResponse,
      response
    );
  }
  return json;
};
